import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useToast } from './toastContext';
import {
    subscribeRejections,
    unseenRollbacks
} from '../features/sync/pushRejections';

/**
 * Say it when a change was thrown away.
 *
 * Offline-first means a write always succeeds on the device, and almost always
 * that promise holds: the queue drains by itself, and a genuine clash with a
 * colleague gets the conflict dialog. The one case that keeps neither promise
 * is a write the SERVER refuses — it comes back with the state it already had,
 * and the change is dropped without a merge and without a dialog (see
 * features/sync/conflictHandler).
 *
 * Silent is right for the machinery and wrong for the person: they typed
 * something, watched it appear, and a second later it is gone. So the dropped
 * change is announced here, once, however many arrive together, and the lasting
 * record stays in the sync dialog.
 *
 * Rendered by both chromes, next to their sync indicator.
 */
export default function RollbackNotice() {
    const { notify } = useToast();
    const [count, setCount] = useState(0);

    // The store is shared by every window of this browser, so this follows a
    // rollback that happened in the one next door as well.
    useEffect(() => {
        const update = () => setCount(unseenRollbacks().length);
        update();
        return subscribeRejections(update);
    }, []);

    useEffect(() => {
        if (count === 0) {
            return;
        }
        // One toast, recounted as more arrive — forty-two of them arriving at
        // once is one piece of news, not forty-two.
        notify(
            <FormattedMessage id="sync.rollbackToast" values={{ count }} />,
            'warning'
        );
    }, [count, notify]);

    return null;
}
